import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPlusLg } from "react-icons/bs";
import { GrMapLocation } from "react-icons/gr";
import { MdEdit, MdLocationOn } from "react-icons/md";
import styled from "styled-components";
import { UiConstants } from "../../../data/UiConstants";
import { selectorMapEditorStore, EditorToolType } from "../../../stores/MapEditorStore";
import { Orientation, Overlay, PopupSubMenuExternalState } from "../../menu/PopupSubMenu";
import { parseFormattedTreeParameter } from "../../../../shared/helper/actionTreeHelper";
import { MapSelector } from "../../mapEditor/MapSelector";
import { undoableMapEditorLoadMap } from "../../../stores/undo/operation/MapEditorLoadMapOp";
import { editorMapStore } from "../../../stores/EditorMapStore";
import { editorStore } from "../../../stores/EditorStore";
import { DynamicMapElementNPCModel } from "../../../../shared/game/dynamicMapElements/DynamicMapElementNPCModel";
import { DynamicMapElementAreaTriggerModel } from "../../../../shared/game/dynamicMapElements/DynamicMapElementAreaTriggerModel";
import { SelectorButton, SelectorButtonContent, SelectorButtonContentFilled } from "./SelectorButton";
export const SelectionButtons = styled.div.withConfig({
  displayName: "Selector__SelectionButtons",
  componentId: "sc-f67da9-0"
})(["overflow-y:scroll;max-height:400px;display:flex;flex-flow:row wrap;gap:10px;align-content:flex-start;::-webkit-scrollbar{display:none;}"]);
export const FilterSelect = styled.select.withConfig({
  displayName: "Selector__FilterSelect",
  componentId: "sc-f67da9-1"
})(["width:240px;max-width:240px;"]);
const TopRow = styled.div.withConfig({
  displayName: "Selector__TopRow",
  componentId: "sc-f67da9-2"
})(["margin-bottom:5px;"]);
export const EditButton = styled.div.withConfig({
  displayName: "Selector__EditButton",
  componentId: "sc-f67da9-3"
})(["position:absolute;left:2px;padding:3px 5px;:hover{background-color:#ebc386;}"]);
const EditorOverlay = styled(Overlay).withConfig({
  displayName: "Selector__EditorOverlay",
  componentId: "sc-f67da9-4"
})(["z-index:", ";"], UiConstants.Z_INDEX_POPUP_SUBMENU + 1);
export const Selector = props => {
  const {
    t
  } = useTranslation();
  const selectedElement = props.currentElement;
  const selectedElementId = props.getElementValue(selectedElement);
  const [termToFilter, setTermToFilter] = useState("");
  const [editorVisible, setEditorVisibility] = useState(false);
  const [popupIsOpen, setPopupIsOpen] = useState(false);
  const selectorWidth = 315;

  function selectAndClose(elementId) {
    props.valueSetter(elementId);
    setEditorVisibility(false);
    setPopupIsOpen(false);
  }

  function openEditorMenu(element) {
    if (props.beforeOpenEditorView) props.beforeOpenEditorView(element);
    setEditorVisibility(true);
  }

  function openEditorForNewElement() {
    props.prepareNewElement();

    if (props.mapSetter) {
      undoableMapEditorLoadMap(selectorMapEditorStore.currentMapStore, props.mapId);
    }

    setEditorVisibility(true);
  }

  function openEditorForElementIdOnMap(elementId) {
    var _map$dynamicMapElemen;

    undoableMapEditorLoadMap(selectorMapEditorStore.currentMapStore, props.mapId);
    selectorMapEditorStore.setTool(EditorToolType.SingleSelect); // Find elementId on map and put selection marker on map on it

    const map = editorMapStore.getOrLoadMapWithMetaData(props.mapId).map;
    let elementOnMapPosition = (_map$dynamicMapElemen = map.dynamicMapElements.find(element => element.$modelId === elementId || element instanceof DynamicMapElementNPCModel && element.viewAreaTriggers.some(viewAreaTrigger => viewAreaTrigger.name === elementId) || element instanceof DynamicMapElementAreaTriggerModel && element.id === elementId)) === null || _map$dynamicMapElemen === void 0 ? void 0 : _map$dynamicMapElemen.position;

    if (!elementOnMapPosition) {
      var _map$interactionTrigg;

      elementOnMapPosition = (_map$interactionTrigg = map.interactionTriggerTiles.find(tile => tile.interactionTriggerData.$modelId === elementId)) === null || _map$interactionTrigg === void 0 ? void 0 : _map$interactionTrigg.position;
    }

    if (elementOnMapPosition) {
      selectorMapEditorStore.setSelectedTilePosition(elementOnMapPosition);
    }

    setEditorVisibility(true);
  }

  function openEditorForElementOnMap(element) {
    const elementId = props.getElementValue(element);
    openEditorForElementIdOnMap(elementId);
  }

  function openEditorForSelectionOnMap() {
    openEditorForElementIdOnMap(selectedElementId);
  }

  function hasEditRights(element) {
    if (editorStore.isMainGameEditor) {
      return true;
    }

    return !props.getElementIsOwnedByAModule || props.getElementIsOwnedByAModule(element);
  }

  function closeEditorMenu() {
    setEditorVisibility(false);
  }

  const selectedElementImage = props.getElementImageUrl ? props.getElementImageUrl(selectedElement) : null;
  const selectedElementIcon = !selectedElementImage && props.getElementIcon && props.getElementIcon(selectedElement);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PopupSubMenuExternalState, {
    isOpen: popupIsOpen,
    setIsOpen: setPopupIsOpen,
    onOpen: props.onPopupOpen,
    orientation: Orientation.Left,
    relativeOffset: 0,
    containerWidth: `${selectorWidth}px`,
    containerHeight: "464px",
    buttonClass: SelectorButton,
    buttonContent: /*#__PURE__*/React.createElement(SelectorButtonContentFilled, {
      image: selectedElementImage,
      icon: selectedElementIcon,
      text: props.buttonText,
      secondaryText: props.buttonSecondaryText,
      flexibleHeight: true
    }),
    buttonInvalid: props.buttonInvalidProvider ? props.buttonInvalidProvider() : false,
    positionFixed: true
  }, /*#__PURE__*/React.createElement(TopRow, null, props.filterGetter && /*#__PURE__*/React.createElement(FilterSelect, {
    value: termToFilter,
    onChange: ({
      target
    }) => setTermToFilter(target.value)
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }), props.filterGetter().map(term => /*#__PURE__*/React.createElement("option", {
    key: term,
    value: term
  }, term))), props.mapSetter && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MapSelector, {
    selectedMapId: props.mapId,
    onSelected: mapId => props.mapSetter(mapId),
    hasEmpty: true,
    hideId: true,
    width: "100%",
    limitedToMapId: props.limitedToMapId
  }))), props.loadingStatusIndicator, !props.loadingStatusIndicator && /*#__PURE__*/React.createElement(SelectionButtons, null, (!props.selectableOnMap || !!props.mapSetter && !!props.mapId && props.mapId !== 0) && /*#__PURE__*/React.createElement(React.Fragment, null, props.selectableOnMap && /*#__PURE__*/React.createElement(SelectorButton, {
    onClick: openEditorForSelectionOnMap
  }, /*#__PURE__*/React.createElement(SelectorButtonContent, null, /*#__PURE__*/React.createElement(GrMapLocation, {
    size: 50
  }), /*#__PURE__*/React.createElement("div", null, t("action_editor.select_on_map")))), !!props.prepareNewElement && (!props.mapSetter || editorMapStore.isUserAllowedToEditMapWithId(props.mapId)) && /*#__PURE__*/React.createElement(SelectorButton, {
    onClick: openEditorForNewElement
  }, /*#__PURE__*/React.createElement(SelectorButtonContent, null, /*#__PURE__*/React.createElement(BsPlusLg, {
    size: 50
  }), /*#__PURE__*/React.createElement("div", null, t(props.newString))))), props.filteredListGetter(termToFilter).map(element => {
    const isTreeParameter = parseFormattedTreeParameter(props.getElementValue(element));
    const editRights = hasEditRights(element);
    const image = props.getElementImageUrl ? props.getElementImageUrl(element) : null;
    const icon = !image && props.getElementIcon && props.getElementIcon(element);
    return /*#__PURE__*/React.createElement(SelectorButton, {
      key: props.getElementValue(element),
      bgColor: selectedElementId === props.getElementValue(element) ? "#dda551" : null,
      onClick: () => selectAndClose(props.getElementValue(element))
    }, /*#__PURE__*/React.createElement(SelectorButtonContentFilled, {
      image: image,
      icon: icon,
      text: props.getElementName(element)
    }, editRights && !isTreeParameter && /*#__PURE__*/React.createElement(React.Fragment, null, !props.selectableOnMap && /*#__PURE__*/React.createElement(EditButton, {
      onClick: e => {
        e.stopPropagation();
        openEditorMenu(element);
      }
    }, /*#__PURE__*/React.createElement(MdEdit, null)), props.selectableOnMap && /*#__PURE__*/React.createElement(EditButton, {
      onClick: e => {
        e.stopPropagation();
        openEditorForElementOnMap(element);
      }
    }, /*#__PURE__*/React.createElement(MdLocationOn, null)))));
  }))), editorVisible && /*#__PURE__*/React.createElement(EditorOverlay, {
    onClick: closeEditorMenu
  }), editorVisible && props.editorView(selectAndClose, closeEditorMenu, selectorWidth, selectedElementId));
};